import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Heart, Quote, ArrowRight, MessageCircle, Send, Loader2, CheckCircle2 } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Wall of Love — renders approved testimonials from /api/testimonials.
 *
 * v34.14 (Feb 2026): Empty state now has an INLINE one-shot share form so
 * visitors can leave a testimonial without leaving the landing page. The
 * full /share-your-voice route still exists as a "more space to write"
 * fallback link below the form. Pre-launch credibility lift without the
 * routing-away cost.
 */
const WallOfLove = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/testimonials/`);
        if (!cancelled) setTestimonials(Array.isArray(res.data) ? res.data : []);
      } catch {
        if (!cancelled) setTestimonials([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // v34.80 (Jun 9 2026) — landing footprint trimmed:
  //   • show at most 3 testimonials with "see all" link
  //   • empty state collapses to a single-line CTA instead of full form
  // Full grid + submit form live at /wall-of-tenants and /share-your-voice.
  const displayed = testimonials.slice(0, 3);
  const isEmpty = !loading && testimonials.length === 0;

  return (
    <section className="py-12 px-4" data-testid="wall-of-love">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 mb-3">
            <Heart className="w-3 h-3 text-emerald-400" />
            <span className="text-[11px] font-semibold tracking-widest uppercase text-emerald-400">
              Wall of love
            </span>
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">
            What early voices are saying
          </h2>
        </div>

        {isEmpty ? (
          <div className="rounded-2xl border border-emerald-500/20 bg-slate-900 px-6 py-5 max-w-2xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-slate-300 text-center sm:text-left">
              No testimonials yet — be the first to leave one.
            </p>
            <Link
              to="/share-your-voice"
              data-testid="wall-empty-cta"
              className="inline-flex items-center gap-2 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 px-4 py-2 font-bold text-sm shrink-0"
            >
              Share your story <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        ) : (
          <>
            <Helmet>
              <script type="application/ld+json">
                {JSON.stringify(testimonials.slice(0, 10).map((t) => ({
                  '@context': 'https://schema.org',
                  '@type': 'Review',
                  itemReviewed: { '@type': 'SoftwareApplication', name: 'CARTIEAI', url: 'https://cartieai.com/', applicationCategory: 'BusinessApplication', operatingSystem: 'Web' },
                  author: { '@type': 'Person', name: t.name },
                  reviewBody: t.quote,
                  ...(t.rating ? { reviewRating: { '@type': 'Rating', ratingValue: t.rating, bestRating: 5 } } : {}),
                })))}
              </script>
            </Helmet>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {displayed.map((t) => (
                <a
                  key={t.id}
                  href={t.linkedin_url || '#'}
                  target={t.linkedin_url ? '_blank' : undefined}
                  rel={t.linkedin_url ? 'noopener noreferrer' : undefined}
                  onClick={(e) => { if (!t.linkedin_url) e.preventDefault(); }}
                  className={`relative rounded-2xl border border-white/10 bg-white/[0.02] p-5 overflow-hidden group ${
                    t.linkedin_url
                      ? 'hover:border-emerald-500/30 hover:bg-white/[0.04] transition-all hover:-translate-y-0.5 cursor-pointer'
                      : 'cursor-default'
                  }`}
                  data-testid={`testimonial-${t.id}`}
                >
                  <Quote className="absolute top-3 right-3 w-8 h-8 text-emerald-500/10" />
                  <p className="relative text-sm text-slate-200 leading-relaxed mb-5 italic">
                    &ldquo;{t.quote}&rdquo;
                  </p>
                  <div className="relative flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-emerald-500/20 to-teal-500/20 border border-emerald-500/30 flex items-center justify-center text-sm font-bold text-emerald-300">
                      {t.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-white truncate">{t.name}</p>
                      <p className="text-[11px] text-slate-400 truncate">
                        {[t.role, t.company].filter(Boolean).join(' \u00b7 ') || 'Early voice'}
                      </p>
                    </div>
                  </div>
                </a>
              ))}
            </div>
            <div className="mt-7 flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
              <Link
                to="/wall-of-tenants"
                data-testid="wall-see-all"
                className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-300 hover:text-emerald-200 group"
              >
                See all customer stories
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </Link>
              <Link
                to="/share-your-voice"
                className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-emerald-400 transition-colors"
                data-testid="add-your-voice-link"
              >
                Want to add yours? <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </>
        )}
      </div>
    </section>
  );
};

// ── Empty state component with inline form ────────────────────────────────
const EmptyWallWithInlineForm = () => {
  const [showForm, setShowForm] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: '', role: '', quote: '', linkedin_url: '' });

  const onChange = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.quote.trim().length < 10) {
      toast.error('Just one sentence — at least 10 characters.');
      return;
    }
    if (form.name.trim().length < 2) {
      toast.error('Please add your name.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/testimonials/submit`, {
        name: form.name.trim(),
        role: form.role.trim() || null,
        quote: form.quote.trim(),
        linkedin_url: form.linkedin_url.trim() || null,
        consent_public_display: true,
      });
      setSubmitted(true);
      toast.success('Got it — thank you! 💚');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Something went wrong. Please try again.';
      toast.error(typeof msg === 'string' ? msg : 'Submission failed.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="py-16 px-4" data-testid="wall-of-love-empty">
      <div className="max-w-3xl mx-auto">
        <div className="relative rounded-2xl border border-white/10 bg-gradient-to-br from-slate-900/60 via-slate-900/30 to-slate-950/60 p-8 sm:p-10 overflow-hidden">
          <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-emerald-500/10 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-20 -left-20 w-56 h-56 rounded-full bg-teal-500/5 blur-3xl pointer-events-none" />

          {submitted ? (
            // ── Post-submit confirmation ──────────────────────────────────
            <div className="relative text-center" data-testid="wall-thanks">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-500/15 border border-emerald-400/40 mb-4">
                <CheckCircle2 className="w-7 h-7 text-emerald-400" />
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold text-white mb-3">
                Thank you, <span className="text-emerald-300">{form.name.split(' ')[0]}</span>.
              </h3>
              <p className="text-sm text-slate-400 leading-relaxed max-w-xl mx-auto">
                Your words mean a lot — they'll show up on this wall once approved. Until then,
                they sit on the founder's desk.
              </p>
              <button
                type="button"
                onClick={() => { setSubmitted(false); setForm({ name: '', role: '', quote: '', linkedin_url: '' }); setShowForm(false); }}
                className="mt-6 text-xs text-slate-500 hover:text-emerald-400 transition-colors"
                data-testid="wall-share-another"
              >
                Share another voice →
              </button>
            </div>
          ) : !showForm ? (
            // ── Initial empty-state card ─────────────────────────────────
            <div className="relative text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-emerald-500/10 border border-emerald-500/20 mb-4">
                <MessageCircle className="w-5 h-5 text-emerald-400" />
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold text-white mb-3">
                The wall is quiet — for now.
              </h3>
              <p className="text-sm sm:text-base text-slate-400 leading-relaxed max-w-xl mx-auto mb-6">
                CARTIEAI is in its earliest days. I'm personally inviting a small group of FinOps
                engineers, DevOps leads, and finance partners to shape what comes next. If that's
                you — I'd love to hear what you think.
              </p>
              <button
                type="button"
                onClick={() => setShowForm(true)}
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold text-sm shadow-[0_0_0_1px_rgba(16,185,129,0.5),0_10px_30px_-8px_rgba(16,185,129,0.6)] hover:shadow-[0_0_0_1px_rgba(16,185,129,0.6),0_14px_40px_-8px_rgba(16,185,129,0.75)] transition-all hover:-translate-y-0.5"
                data-testid="share-voice-cta"
              >
                <Heart className="w-4 h-4 fill-slate-950" />
                Be our first voice
                <ArrowRight className="w-4 h-4" />
              </button>
              <p className="text-[11px] text-slate-400 mt-4">
                Takes 30 seconds. One sentence is enough.
              </p>
            </div>
          ) : (
            // ── Inline form ──────────────────────────────────────────────
            <form onSubmit={onSubmit} className="relative" data-testid="wall-inline-form">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-full bg-emerald-500/15 border border-emerald-400/30 flex items-center justify-center">
                    <Heart className="w-4 h-4 text-emerald-300" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-white">One sentence is enough.</p>
                    <p className="text-[11px] text-slate-400">Approved before it goes public.</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="text-xs text-slate-500 hover:text-slate-300"
                  data-testid="wall-form-cancel"
                >
                  Cancel
                </button>
              </div>

              {/* Quote — the hero field */}
              <label className="block">
                <span className="sr-only">Your one-sentence take</span>
                <textarea
                  value={form.quote}
                  onChange={(e) => onChange('quote', e.target.value)}
                  placeholder="One honest sentence about CARTIE — what stood out, what's missing, what you'd want next…"
                  rows={3}
                  maxLength={500}
                  className="w-full px-4 py-3 rounded-xl bg-slate-950 border border-white/10 text-white placeholder:text-slate-500 text-sm leading-relaxed resize-none focus:border-emerald-500/50 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 transition-all"
                  data-testid="wall-form-quote"
                  required
                />
                <div className="mt-1 text-right text-[10px] font-mono tabular-nums text-slate-500">
                  {form.quote.length}/500
                </div>
              </label>

              {/* Identity row */}
              <div className="grid sm:grid-cols-2 gap-3 mt-3">
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => onChange('name', e.target.value)}
                  placeholder="Your name *"
                  maxLength={100}
                  className="px-4 py-2.5 rounded-xl bg-slate-950 border border-white/10 text-white placeholder:text-slate-500 text-sm focus:border-emerald-500/50 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 transition-all"
                  data-testid="wall-form-name"
                  required
                />
                <input
                  type="text"
                  value={form.role}
                  onChange={(e) => onChange('role', e.target.value)}
                  placeholder="Role · Company (optional)"
                  maxLength={120}
                  className="px-4 py-2.5 rounded-xl bg-slate-950 border border-white/10 text-white placeholder:text-slate-500 text-sm focus:border-emerald-500/50 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 transition-all"
                  data-testid="wall-form-role"
                />
              </div>

              {/* Optional LinkedIn */}
              <input
                type="url"
                value={form.linkedin_url}
                onChange={(e) => onChange('linkedin_url', e.target.value)}
                placeholder="LinkedIn URL (optional · so we can credit you)"
                maxLength={200}
                className="w-full mt-3 px-4 py-2.5 rounded-xl bg-slate-950 border border-white/10 text-white placeholder:text-slate-500 text-sm focus:border-emerald-500/50 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 transition-all"
                data-testid="wall-form-linkedin"
              />

              {/* Submit */}
              <div className="mt-5 flex items-center justify-between gap-3 flex-wrap">
                <p className="text-[11px] text-slate-500">
                  By submitting you consent to public display. We'll never share your email.
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-emerald-500 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-semibold text-sm shadow-lg shadow-emerald-500/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:-translate-y-0.5"
                  data-testid="wall-form-submit"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Sending…
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Send it
                    </>
                  )}
                </button>
              </div>

              {/* Long-form fallback */}
              <p className="mt-4 text-center text-[11px] text-slate-500">
                Want more space? <Link to="/share-your-voice" className="text-emerald-400 hover:text-emerald-300 underline-offset-2 hover:underline" data-testid="wall-form-longform-link">Use the full form →</Link>
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

export default WallOfLove;
